/**
 * ContactSalesModal — v147.99.31 (Feb 2026)
 *
 * Lead-capture modal for the Corporate (Multi-Branch) plan. Posts to
 * `/api/org/contact-sales`, which is public (no auth) so anonymous
 * enterprise leads can reach us too. v149.68 — enquiries show in
 * Admin → Corporate Leads; `source` says which page the form was on.
 */
import { useState } from "react";
import { X, Loader2, Building2, Check } from "lucide-react";
import { api } from "../lib/api";
import { toast } from "sonner";

export default function ContactSalesModal({ open, onClose, source }) {
  const [form, setForm] = useState({
    company_name: "",
    contact_name: "",
    contact_email: "",
    contact_phone: "",
    branches_count: "",
    notes: "",
  });
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    if (busy) return;
    setBusy(true);
    try {
      await api.post("/org/contact-sales", {
        ...form,
        branches_count: form.branches_count ? Number(form.branches_count) : null,
        source,
      });
      setDone(true);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Couldn't send — please try again.");
    } finally {
      setBusy(false);
    }
  };

  if (!open) return null;
  return (
    <div
      className="fixed inset-0 z-[9600] bg-ink/70 backdrop-blur-sm flex items-end sm:items-center justify-center sm:p-4"
      onClick={onClose}
      data-testid="contact-sales-modal"
    >
      <div
        onClick={(e) => e.stopPropagation()}
        className="w-full sm:max-w-md bg-white border-t-4 sm:border-4 border-ink shadow-brutal flex flex-col max-h-[90dvh] rounded-t-2xl sm:rounded-none overflow-hidden"
      >
        <div className="flex-shrink-0 flex items-center justify-between p-3 border-b-2 border-ink bg-sun">
          <div className="flex items-center gap-2">
            <Building2 className="w-4 h-4" strokeWidth={2.5} />
            <p className="font-display font-black">Corporate (Multi-Branch)</p>
          </div>
          <button type="button" onClick={onClose} data-testid="contact-sales-close"
            className="border-2 border-ink bg-white p-1.5 shadow-brutalSm hover:translate-y-[1px]">
            <X className="w-4 h-4" strokeWidth={3} />
          </button>
        </div>

        <div className="flex-1 overflow-y-auto p-4">
          {done ? (
            <div className="text-center py-8 space-y-3" data-testid="contact-sales-done">
              <div className="inline-flex w-14 h-14 items-center justify-center border-2 border-ink bg-mint">
                <Check className="w-6 h-6" strokeWidth={3} />
              </div>
              <p className="font-display font-black text-xl">Thanks — we'll be in touch!</p>
              <p className="text-sm text-ink/70">
                Our sales team will reach out within 1 business day with a custom quote for your chain.
              </p>
              <button type="button" onClick={onClose}
                className="mt-2 border-2 border-ink bg-white px-4 py-2 font-bold text-sm shadow-brutalSm">
                Close
              </button>
            </div>
          ) : (
            <form onSubmit={submit} className="space-y-3" data-testid="contact-sales-form">
              <p className="text-sm text-ink/70">
                Tell us about your chain — we'll send back a quote tailored to your branch count and volume.
              </p>
              <Field label="Company name" required
                value={form.company_name}
                onChange={(v) => set("company_name", v)}
                testid="cs-company-name"
              />
              <Field label="Your name" required
                value={form.contact_name}
                onChange={(v) => set("contact_name", v)}
                testid="cs-contact-name"
              />
              <Field label="Work email" required type="email"
                value={form.contact_email}
                onChange={(v) => set("contact_email", v)}
                testid="cs-contact-email"
              />
              <Field label="Phone (optional)"
                value={form.contact_phone}
                onChange={(v) => set("contact_phone", v)}
                testid="cs-contact-phone"
              />
              <Field label="How many branches?" type="number"
                value={form.branches_count}
                onChange={(v) => set("branches_count", v)}
                placeholder="e.g. 12"
                testid="cs-branches-count"
              />
              <label className="block">
                <span className="text-xs font-bold uppercase tracking-wide text-ink/70">
                  Anything specific? (optional)
                </span>
                <textarea rows={3} value={form.notes} onChange={(e) => set("notes", e.target.value)}
                  className="w-full border-2 border-ink px-2 py-1.5 text-sm bg-white mt-1"
                  placeholder="POS integration, custom domain, migration help…"
                  data-testid="cs-notes"
                />
              </label>
              <button type="submit" disabled={busy}
                data-testid="cs-submit"
                className="w-full border-2 border-ink bg-coral text-white px-4 py-3 font-black text-base shadow-brutalSm inline-flex items-center justify-center gap-2 disabled:opacity-60">
                {busy && <Loader2 className="w-4 h-4 animate-spin" />}
                Request a quote
              </button>
            </form>
          )}
        </div>
      </div>
    </div>
  );
}

function Field({ label, value, onChange, required, type = "text", placeholder, testid }) {
  return (
    <label className="block">
      <span className="text-xs font-bold uppercase tracking-wide text-ink/70">
        {label}{required && <span className="text-coral"> *</span>}
      </span>
      <input
        type={type}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        required={required}
        placeholder={placeholder}
        data-testid={testid}
        className="w-full border-2 border-ink px-2 py-1.5 text-sm bg-white mt-1"
      />
    </label>
  );
}
